import { NAV } from '../nav';
import { PAGES, docPath } from '../pages/docs/registry';
import { fold, type SearchItem } from './search-core';

export interface CatalogModel {
  id: string;
  display_name?: string;
  provider?: string;
  family?: string;
  modality?: string;
  pricing?: { input_per_1m?: number; per_image?: number; per_second_720p?: number } | null;
}
export interface ProjectLite {
  id: string;
  name: string;
  environment: string;
}
export interface KeyLite {
  id: string;
  name: string;
  key_prefix: string;
  project_name: string;
  status: string;
}

type Draft = Omit<SearchItem, 'text'> & { keywords?: string };

function item({ keywords = '', ...rest }: Draft): SearchItem {
  return { ...rest, text: fold(`${rest.title} ${rest.strict ? '' : (rest.subtitle ?? '')} ${keywords}`) };
}

export function pageItems(): SearchItem[] {
  return NAV.flatMap((section) =>
    section.links.map((link) =>
      item({ id: `page:${link.to}`, group: 'page', title: link.label, subtitle: section.section, to: link.to, icon: link.icon, keywords: link.keywords, boost: 2 }),
    ),
  );
}

export interface ActionContext {
  signOut: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

export function actionItems(ctx: ActionContext): SearchItem[] {
  return [
    item({ id: 'act:new-key', group: 'action', title: 'Buat API key baru', to: '/api-keys?baru=1', icon: 'plus', keywords: 'tambah kunci token', boost: 1 }),
    item({ id: 'act:topup', group: 'action', title: 'Isi saldo', to: '/wallet', icon: 'wallet', keywords: 'top up qris bayar bonus', boost: 1 }),
    item({ id: 'act:playground', group: 'action', title: 'Coba model di Playground', to: '/playground', icon: 'terminal', keywords: 'chat uji prompt' }),
    item({ id: 'act:usage', group: 'action', title: 'Lihat log request', to: '/usage', icon: 'bars', keywords: 'pemakaian biaya request terakhir' }),
    item({ id: 'act:theme-dark', group: 'action', title: 'Pakai tema gelap', icon: 'moon', run: () => ctx.setTheme('dark'), keywords: 'dark mode tampilan' }),
    item({ id: 'act:theme-light', group: 'action', title: 'Pakai tema terang', icon: 'sun', run: () => ctx.setTheme('light'), keywords: 'light mode tampilan' }),
    item({ id: 'act:theme-system', group: 'action', title: 'Ikuti tema sistem', icon: 'monitor', run: () => ctx.setTheme('system'), keywords: 'otomatis tampilan' }),
    item({ id: 'act:logout', group: 'action', title: 'Keluar', icon: 'logout', run: ctx.signOut, keywords: 'logout sign out' }),
    item({ id: 'act:delete-account', group: 'action', title: 'Hapus akun', to: '/settings?hapus=1', icon: 'warn', keywords: 'delete account tutup akun hapus permanen' }),
  ];
}

const MODALITY: Record<string, string> = { text: 'teks chat', image: 'gambar image', video: 'video', embedding: 'embedding embeddings vektor rag' };

export function modelItems(models: CatalogModel[], money: (value: number) => string): SearchItem[] {
  return models.map((model) => {
    const p = model.pricing ?? {};
    const price =
      p.input_per_1m !== undefined
        ? `${money(p.input_per_1m)}/1jt masukan`
        : p.per_image !== undefined
          ? `${money(p.per_image)}/gambar`
          : p.per_second_720p !== undefined
            ? `${money(p.per_second_720p)}/detik`
            : '';
    return item({
      id: `model:${model.id}`,
      group: 'model',
      title: model.display_name ?? model.id,
      subtitle: [model.id, price].filter(Boolean).join(' · '),
      to: model.modality === 'embedding' ? '/docs/embeddings' : `/playground?model=${encodeURIComponent(model.id)}`,
      logo: { model: model.id, provider: model.provider, family: model.family },
      keywords: `${model.provider ?? ''} ${model.family ?? ''} ${MODALITY[model.modality ?? 'text'] ?? ''}`,
    });
  });
}

export function projectItems(projects: ProjectLite[]): SearchItem[] {
  return projects.map((project) =>
    item({
      id: `project:${project.id}`,
      group: 'project',
      title: project.name,
      subtitle: project.environment === 'PRODUCTION' ? 'Produksi' : 'Pengembangan',
      to: `/projects/${project.id}`,
      icon: 'folder',
      keywords: 'proyek project',
    }),
  );
}

export function keyItems(keys: KeyLite[]): SearchItem[] {
  return keys
    .filter((key) => key.status === 'ACTIVE')
    .map((key) =>
      item({
        id: `key:${key.id}`,
        group: 'key',
        title: key.name,
        subtitle: `${key.key_prefix}•••• · ${key.project_name}`,
        to: `/api-keys?q=${encodeURIComponent(key.name)}`,
        icon: 'key',
        keywords: `${key.key_prefix} api key kunci`,
      }),
    );
}

export function docItems(): SearchItem[] {
  return PAGES.flatMap((page) => {
    const pageTitle = page.slug ? page.title : 'Mulai cepat';
    return [
      item({ id: `doc:${page.slug || 'home'}`, group: 'doc', title: pageTitle, subtitle: page.group, to: docPath(page.slug), icon: page.icon, keywords: `${page.nav} ${page.blurb} ${page.keywords} dokumentasi docs` }),
      ...page.sections.map(([id, title]) =>
        item({ id: `doc:${page.slug}#${id}`, group: 'doc', title, subtitle: `Bagian di ${pageTitle}`, to: `${docPath(page.slug)}#${id}`, icon: 'list', strict: true }),
      ),
    ];
  });
}

export function publicPageItems(): SearchItem[] {
  return [
    item({ id: 'pub:home', group: 'page', title: 'Beranda', to: '/', icon: 'home', keywords: 'landing partner ai', boost: 1 }),
    item({ id: 'pub:pricing', group: 'page', title: 'Model dan harga', to: '/#model', icon: 'grid', keywords: 'katalog tarif' }),
    item({ id: 'pub:login', group: 'page', title: 'Masuk', to: '/login', icon: 'user', keywords: 'login sign in portal' }),
    item({ id: 'pub:register', group: 'page', title: 'Daftar akun', to: '/login?daftar=1', icon: 'plus', keywords: 'register sign up buat akun' }),
  ];
}
